import { clientConfigSchema, type ClientConfig } from '@/types';
import { PLATFORM_QUOTA_INITIAL_VALUES } from '@/services/platformSettingsService';

/**
 * FEAT-239 — single source of truth for the client-config editing surfaces.
 *
 * Every form-editable config field is declared ONCE here and consumed by:
 *   - ClientConfigForm (shared by Update + Create Client Config pages)
 *   - BulkUpdateClientConfig (field list derived from `bulk`/`allowUnset`)
 *   - ConfigHygienePanel (DEPRECATED_KEYS + unknown-key detection)
 *
 * Add new config fields HERE, not to the pages. A field left out of this
 * registry is invisible to the forms but untouched by saves (Update writes
 * a per-key diff, never the whole record).
 */

export type SectionId =
  | 'core'
  | 'apps'
  | 'chat'
  | 'integrations'
  | 'storage'
  | 'automations'
  | 'bedrock'
  | 'security'
  | 'development';

export interface ConfigSection {
  id: SectionId;
  title: string;
  description?: string;
}

export const CONFIG_SECTIONS: ConfigSection[] = [
  { id: 'core', title: 'Core', description: 'Region, branding and Numa Ops' },
  { id: 'apps', title: 'Apps', description: 'Which Numa apps are deployed' },
  { id: 'chat', title: 'Chat & Agents', description: 'Workspace chat, model access, agent builder' },
  { id: 'integrations', title: 'Integrations', description: 'Pipedream and native connectors' },
  { id: 'storage', title: 'Storage & Files', description: 'Knowledge base backend and Q provisioning' },
  { id: 'automations', title: 'Automations', description: 'Schedules, event triggers and their quotas' },
  { id: 'bedrock', title: 'Bedrock & Billing', description: 'Quota sharing and inference routing' },
  { id: 'security', title: 'Security & SSO', description: 'Sign-on, MFA and user feature groups' },
  {
    id: 'development',
    title: 'Development',
    description: 'Internal & in-development features — several are not customer-ready',
  },
];

/** Form-level value for a single field. tri-state selects use '' | 'true' | 'false'. */
export type FormFieldValue = string | boolean;
export type FormValues = Record<string, FormFieldValue>;

export type ConfigFieldType = 'switch' | 'text' | 'number' | 'select' | 'tri-state';

export interface FieldDef {
  key: string;
  label: string;
  section: SectionId;
  type: ConfigFieldType;
  /** UI display default — what the field shows when the key is absent from the record. */
  default: FormFieldValue;
  /**
   * Create-page initial value when it should differ from the schema default
   * (e.g. flags ~95% of the fleet turns on). Because it differs from
   * `default`, Create writes the key EXPLICITLY — deploy-time schema defaults
   * are unchanged, so existing records missing the key are unaffected.
   */
  createDefault?: FormFieldValue;
  help?: string;
  options?: { label: string; value: string }[];
  placeholder?: string;
  /** Live "Leave empty to inherit (Platform Settings → N)" placeholder for quota fields. */
  inheritKey?: keyof typeof PLATFORM_QUOTA_INITIAL_VALUES;
  inheritSuffix?: string;
  /** Hide the field unless the predicate over current form values passes. */
  visibleWhen?: (values: FormValues) => boolean;
  /** Grey the control out (still visible, not editable) while the predicate over current form values passes. */
  disabledWhen?: (values: FormValues) => boolean;
  /** Info note rendered under the field while `disabledWhen` holds, explaining why it is disabled. */
  disabledNote?: string;
  /**
   * `switch` fields only: when a sibling change makes `disabledWhen` hold, force
   * the value OFF instead of leaving it stuck visually "On" while greyed. Use for
   * toggles that are meaningless once disabled (e.g. the model picker once only
   * one model is available). Mirrors infra's derived flag, which already gates
   * the runtime value on the same condition.
   */
  disabledForcesOff?: boolean;
  /**
   * `select` fields whose option list depends on other form values (e.g. the
   * default-model dropdown narrows with Bedrock Models Only). When present it
   * overrides the static `options`. The form also keeps the field's value valid:
   * if a sibling change drops the current choice from the list, it snaps to the
   * first option.
   */
  dynamicOptions?: (values: FormValues) => { label: string; value: string; disabled?: boolean }[];
  /**
   * Custom "effective current value" for diffing — what the deployment uses
   * today when the key is absent. Use for fields whose absent-value is DERIVED
   * from sibling flags (e.g. `defaultChatModel`), so a pristine record shows no
   * spurious pending change. Must agree with `hydrate` for the same record.
   */
  effectiveCurrent?: (config: Partial<ClientConfig> | undefined) => unknown;
  /**
   * Key of the parent switch this field belongs to. Children render inside an
   * indented sub-panel directly under their parent (same styling as the
   * Automations sub-options). Combine with `visibleWhen` to gate visibility.
   */
  childOf?: string;
  /** Custom hydration from a config record (e.g. legacy-key fallbacks). */
  hydrate?: (config: Partial<ClientConfig> | undefined) => FormFieldValue;
  /** Update mode: always write the key when missing from the record, even at default. */
  alwaysWrite?: boolean;
  /** Include in the Bulk Update tool. Defaults to true. */
  bulk?: boolean;
  /** Validation for text/number fields, run on submit against the raw string. */
  validate?: (raw: string) => string | null;
  /** Warning Alert rendered under the field whenever it holds a non-default value. */
  warnWhenSet?: string;
}

// ---------------------------------------------------------------------------
// Workspace chat model tiers — default-model dropdown + picker availability.
//
// One bit used to govern all this (Model Selection on/off + Bedrock Models
// Only), which was redundant. The model is now: Bedrock Models Only constrains
// the AVAILABLE set (drops the non-AWS Standard tier); Default Model picks the
// platform default from that set; the end-user picker ("Workspace Chat Model
// Selection") is only offerable when >1 model is available.
//
// Keep the tiers / derivation / availability IN SYNC with:
//   - infra/stacks/numa-client-stack.ts (emits DEFAULT_CHAT_MODEL + the derived
//     WORKSPACE_CHAT_MODEL_SELECTION picker flag)
//   - services/numa-workspace-agent/.../sdk_config.py (platform_default_model)
//   - numa-frontend workspaceChatTypes + ChatInput (curated selector + filter)
// ---------------------------------------------------------------------------

export type ChatModelTier = 'light' | 'standard' | 'premium' | 'expert';

/** The non-AWS relay tiers — both Lite and Standard route through the deployer
 *  relay (OpenRouter/Novita), NOT AWS Bedrock, so Bedrock Models Only drops both. */
export const NON_AWS_CHAT_MODEL_TIERS: readonly ChatModelTier[] = ['light', 'standard'];

/** Dropdown labels for each tier. Lite + Standard = Numa relay models (non-AWS);
 *  Premium = Sonnet 4.6 and Expert = Opus 4.6, both on AWS Bedrock. */
export const CHAT_MODEL_TIER_LABELS: Record<ChatModelTier, string> = {
  light: 'Lite — Numa Lite Model (cheapest, non-AWS)',
  standard: 'Standard — Numa Standard Model (everyday, non-AWS)',
  premium: 'Premium — Sonnet 4.6 (AWS Bedrock)',
  expert: 'Expert — Opus 4.6 (AWS Bedrock)',
};

/** Whether the client surfaces the Expert (Opus) tier. Mirrors the chat gating
 *  (Expert is developer-mode only). The portal form has `devInstance` but not
 *  `publicDemo`, so this is an admin-side approximation of DEVELOPER_MODE
 *  (= devInstance && !publicDemo); the runtime authority stays the frontend. */
function expertTierAvailable(values: FormValues): boolean {
  return values.devInstance === true;
}

/** The model tiers available to a client given its flags, cheapest-first.
 *  Bedrock Models Only drops the non-AWS relay tiers (Lite + Standard). */
export function availableChatModelTiers(values: FormValues): ChatModelTier[] {
  const tiers: ChatModelTier[] = [];
  if (values.bedrockModelsOnly !== true) {
    tiers.push('light');
    tiers.push('standard');
  }
  tiers.push('premium');
  if (expertTierAvailable(values)) tiers.push('expert');
  return tiers;
}

/** True when >1 model is available, so the in-chat picker is meaningful and the
 *  "Workspace Chat Model Selection" toggle can be turned on. */
export function chatModelPickerOfferable(values: FormValues): boolean {
  return availableChatModelTiers(values).length > 1;
}

/** Options for the Default Model dropdown. Premium is always available; Lite and
 *  Standard are dropped under Bedrock Models Only (both non-AWS relay tiers —
 *  toggle that off here to restore them); Expert (Opus) is always shown but GREYED
 *  OUT unless this is a Development instance, so admins can see the tier exists and
 *  why it's gated. The greyed (disabled) tiers are never selectable and the form
 *  snaps off them. */
export function chatModelDropdownOptions(values: FormValues): { label: string; value: string; disabled?: boolean }[] {
  const dev = expertTierAvailable(values);
  const options: { label: string; value: string; disabled?: boolean }[] = [];
  if (values.bedrockModelsOnly !== true) {
    options.push({ label: CHAT_MODEL_TIER_LABELS.light, value: 'light' });
    options.push({ label: CHAT_MODEL_TIER_LABELS.standard, value: 'standard' });
  }
  options.push({ label: CHAT_MODEL_TIER_LABELS.premium, value: 'premium' });
  options.push({
    label: dev ? CHAT_MODEL_TIER_LABELS.expert : `${CHAT_MODEL_TIER_LABELS.expert} — Development instances only`,
    value: 'expert',
    disabled: !dev,
  });
  return options;
}

/** The tier a record resolves to when `defaultChatModel` is absent: **Standard**
 *  (the everyday tier) unless Bedrock Models Only is on — then Premium (Sonnet),
 *  since Lite + Standard are non-AWS. Independent of the picker toggle. Lite is
 *  opt-in only (never the derived default). Accepts form values or a raw record. */
export function resolveDefaultChatModel(source: FormValues | Partial<ClientConfig> | undefined): ChatModelTier {
  const v = (source ?? {}) as Record<string, unknown>;
  const explicit = v.defaultChatModel;
  if (explicit === 'light' || explicit === 'standard' || explicit === 'premium' || explicit === 'expert')
    return explicit;
  return v.bedrockModelsOnly === true ? 'premium' : 'standard';
}

// ---------------------------------------------------------------------------
// Shared option constants (previously duplicated in Update/Create/Bulk pages)
// ---------------------------------------------------------------------------

export const REGION_OPTIONS = [
  { label: 'US East (N. Virginia) us-east-1', value: 'us-east-1' },
  { label: 'Asia Pacific (Sydney) ap-southeast-2', value: 'ap-southeast-2' },
  { label: 'Asia Pacific (Jakarta) ap-southeast-3', value: 'ap-southeast-3' },
];

// Regions where Bedrock AgentCore is available (for cross-region AgentCore deployments)
export const AGENTCORE_REGION_OPTIONS = [
  { label: 'US East (N. Virginia) us-east-1', value: 'us-east-1' },
  { label: 'Asia Pacific (Sydney) ap-southeast-2', value: 'ap-southeast-2' },
  { label: 'Asia Pacific (Singapore) ap-southeast-1', value: 'ap-southeast-1' },
  { label: 'Asia Pacific (Tokyo) ap-northeast-1', value: 'ap-northeast-1' },
  { label: 'Asia Pacific (Seoul) ap-northeast-2', value: 'ap-northeast-2' },
  { label: 'Asia Pacific (Mumbai) ap-south-1', value: 'ap-south-1' },
];

// All apps from infrastructure appLibrary + devAppLibrary (sorted alphabetically)
// Matches infra/stacks/numa-client-stack.ts appLibrary and devAppLibrary
export const ALL_APPS = [
  'beyond-expectations',
  'candidate-screening',
  'company-profile',
  'contract-analysis',
  'council-recourse-consents',
  'costing-calculator',
  'data-analysis',
  'document-summariser',
  'e2e-test',
  'financial-analysis',
  'gdsr-assessment',
  'infringement-review',
  'meeting-analyser',
  'nolia',
  'nzsba-policy-builder',
  'policy-drafter',
  'policy-reviewer',
  'procurement-rfp-assessment',
  'rfp-response-comparison',
  'tor-assessment',
];

// Production apps only (isProdApp: true in infra/stacks/numa-client-stack.ts)
export const PROD_APPS = [
  'candidate-screening',
  'company-profile',
  'contract-analysis',
  'document-summariser',
  'financial-analysis',
  'meeting-analyser',
  'policy-drafter',
  'policy-reviewer',
];

// ---------------------------------------------------------------------------
// Field validation helpers
// ---------------------------------------------------------------------------

const validateMinInterval = (raw: string): string | null => {
  if (!raw) return null;
  const val = parseInt(raw, 10);
  if (isNaN(val) || val < 5 || val > 1440) {
    return 'Minimum Automation Interval must be a whole number between 5 and 1440 minutes';
  }
  return null;
};

const validatePositiveInt =
  (label: string) =>
  (raw: string): string | null => {
    if (!raw) return null;
    const n = parseInt(raw, 10);
    if (isNaN(n) || !Number.isInteger(n) || n <= 0) return `${label} must be a positive integer`;
    return null;
  };

const schedulingOn = (v: FormValues) => v.scheduling === true;
const triggersOn = (v: FormValues) => v.scheduling === true && v.eventTriggers === true;
const voiceOn = (v: FormValues) => v.numaVoice === true;

// ---------------------------------------------------------------------------
// The registry. On-screen order within a section = array order.
// The apps deployment control and the groups feature checklists are bespoke
// components (see ClientConfigForm) — everything else renders from here.
// ---------------------------------------------------------------------------

export const FIELDS: FieldDef[] = [
  // --- Core ---
  {
    key: 'region',
    label: 'Region',
    section: 'core',
    type: 'select',
    default: 'us-east-1',
    options: REGION_OPTIONS,
    help: 'AWS region for deployment',
  },
  {
    key: 'agentCoreRegion',
    label: 'AgentCore Region',
    section: 'core',
    type: 'select',
    default: '',
    options: [{ label: 'Same as deployment region (default)', value: '' }, ...AGENTCORE_REGION_OPTIONS],
    help: 'Only set this if the deployment region does not support Bedrock AgentCore. Consult a developer before changing.',
    warnWhenSet:
      'Cross-region AgentCore adds latency and complexity. Do not change this without consulting a developer.',
  },
  {
    key: 'brandingProviderEnabled',
    label: 'Branding',
    section: 'core',
    type: 'switch',
    default: false,
    createDefault: true, // 98% of production clients have it on; none ever off

    help: 'Enable custom branding UI and runtime asset loading',
  },

  // --- Apps: deployment mode is a bespoke radio/checklist (see ClientConfigForm).
  //     No registry-driven Apps fields — v2Apps now lives in the Development section. ---

  // --- Chat & Agents ---
  {
    key: 'numaWorkspaceChat',
    label: 'Numa Workspace Chat',
    section: 'chat',
    type: 'switch',
    default: true,
    help: 'Whether the stack has chat at all. On: deploys the workspace chat agent and the /chat UI. Off: no chat — apps, Ops, etc. still work.',
  },
  {
    key: 'bedrockModelsOnly',
    label: 'Bedrock Models Only',
    section: 'chat',
    type: 'switch',
    default: false,
    help: 'Restrict Workspace Chat to AWS Bedrock models only (no external routing). Removes the non-AWS relay tiers (Lite + Standard) from the available models below and the Default Model dropdown. Use for customers requiring AWS-only inference.',
  },
  {
    key: 'defaultChatModel',
    label: 'Default Chat Model',
    section: 'chat',
    type: 'select',
    default: 'standard',
    dynamicOptions: (values) => chatModelDropdownOptions(values),
    hydrate: (config) => resolveDefaultChatModel(config),
    effectiveCurrent: (config) => resolveDefaultChatModel(config),
    help: 'Default model for Workspace Chat — the pre-selected tier when the picker is on, and the only model when it is off. Lite + Standard are the cheap non-AWS tiers; Premium (Sonnet) + Expert (Opus) are on Bedrock. Unset → Standard, unless Bedrock Models Only is on (then Premium/Sonnet).',
  },
  {
    key: 'workspaceChatModelSelection',
    label: 'Workspace Chat Model Selection',
    section: 'chat',
    type: 'switch',
    default: true,
    help: 'Show the in-chat model picker so users can switch between the available models. Only has effect when more than one model is available — with Bedrock Models Only on and no Expert tier, only Sonnet is available, so there is nothing to pick.',
    disabledWhen: (values) => !chatModelPickerOfferable(values),
    // Collapsing to a single model auto-flips this OFF (not just greyed-on).
    disabledForcesOff: true,
    disabledNote:
      'Only one model is available for this client, so there is nothing for users to pick. Add a second model to offer the picker — turn Bedrock Models Only off (adds the Lite + Standard tiers), or use a Development instance (adds the Expert tier).',
  },
  {
    key: 'agents',
    label: 'Agents',
    section: 'chat',
    type: 'switch',
    default: false,
    createDefault: true, // 93% of production clients have it on

    help: 'Enable Agents UI and related functionality',
  },

  // --- Integrations ---
  {
    key: 'pipedreamIntegrations',
    label: 'Pipedream Integrations',
    section: 'integrations',
    type: 'switch',
    default: false,
    createDefault: true, // 92% of production clients have it on

    help: 'Enable external API integrations',
  },
  {
    key: 'dataConnectorsEnabled',
    label: 'Native Integrations',
    section: 'integrations',
    type: 'switch',
    default: false,
    help: "Surface Arcanum's native (first-party OAuth/PAT) integrations alongside Pipedream-backed ones in the unified Integrations surface",
  },

  // --- Storage & Files ---
  {
    key: 'preferredKnowledgeBase',
    label: 'Numa Files backend',
    section: 'storage',
    type: 'select',
    default: 'bedrock',
    options: [
      { label: 'Bedrock', value: 'bedrock' },
      { label: 'Q Business', value: 'q' },
      { label: 'None (disabled)', value: 'none' },
    ],
    alwaysWrite: true,
    help: 'Indexing backend that powers Numa Files search for this client',
  },
  {
    key: 'provisionQResources',
    label: 'Provision Q Resources',
    section: 'storage',
    type: 'switch',
    default: false,
    alwaysWrite: true,
    help: 'Provision Q Business resources in this account',
  },

  // --- Automations ---
  {
    key: 'scheduling',
    label: 'Agent Automations',
    section: 'automations',
    type: 'switch',
    default: false,
    help: 'Enable agent automations (schedules + triggers) and notifications. The sub-options below only apply when this is on.',
  },
  {
    key: 'eventTriggers',
    label: 'Event Triggers',
    section: 'automations',
    type: 'switch',
    default: false,
    visibleWhen: schedulingOn,
    // Read from eventTriggers first (authoritative post-rebase), fall back to
    // the legacy `triggers` key for unmigrated records, then default false.
    hydrate: (cfg) => {
      const c = cfg as { eventTriggers?: boolean; triggers?: boolean } | undefined;
      return c?.eventTriggers ?? c?.triggers ?? false;
    },
    help: 'Sub-flag of Agent Automations. When off, hides the event-trigger builder for users, the trigger admin tab, and the trigger quota fields below. Cron schedules continue to work.',
  },
  {
    key: 'schedulingMinIntervalMinutes',
    label: 'Minimum Automation Interval (minutes)',
    section: 'automations',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'schedulingMinIntervalMinutes',
    inheritSuffix: ' min',
    validate: validateMinInterval,
    help: 'Minimum allowed interval between automation runs. Applies to schedules. Leave empty to inherit platform default.',
  },
  {
    key: 'maxRunsPerCompanyPerMonth',
    label: 'Max Schedule Runs / Company / Month',
    section: 'automations',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxRunsPerCompanyPerMonth',
    validate: validatePositiveInt('Max schedule runs / company / month'),
    help: 'Hard cap on tenant-wide schedule runs per month. Always enforced — schedules above this are rejected at creation regardless of admin approval.',
  },
  {
    key: 'maxRunsPerUserPerMonth',
    label: 'Max Schedule Runs / User / Month',
    section: 'automations',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxRunsPerUserPerMonth',
    validate: validatePositiveInt('Max schedule runs / user / month'),
    help: 'Above this triggers admin approval (when enabled) or hard rejection.',
  },
  {
    key: 'maxTriggerRunsPerCompanyPerMonth',
    label: 'Max Trigger Runs / Company / Month',
    section: 'automations',
    type: 'number',
    default: '',
    visibleWhen: triggersOn,
    inheritKey: 'maxTriggerRunsPerCompanyPerMonth',
    validate: validatePositiveInt('Max trigger runs / company / month'),
    help: 'Hard cap on tenant-wide trigger fires per month. Counted at fire time.',
  },
  {
    key: 'maxTriggerRunsPerUserPerMonth',
    label: 'Max Trigger Runs / User / Month',
    section: 'automations',
    type: 'number',
    default: '',
    visibleWhen: triggersOn,
    inheritKey: 'maxTriggerRunsPerUserPerMonth',
    validate: validatePositiveInt('Max trigger runs / user / month'),
    help: 'Over-cap fires are dropped silently with a one-shot per-month notification to the owner.',
  },
  {
    key: 'maxConcurrentActiveSchedulesPerCompany',
    label: 'Max Concurrent Active Automations / Company',
    section: 'automations',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxConcurrentActiveSchedulesPerCompany',
    validate: validatePositiveInt('Max concurrent active automations / company'),
    help: 'Hard tenant-wide cap on simultaneously active automations (cron schedules + event triggers).',
  },
  {
    key: 'maxConcurrentActiveSchedulesPerUser',
    label: 'Max Concurrent Active Automations / User',
    section: 'automations',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxConcurrentActiveSchedulesPerUser',
    validate: validatePositiveInt('Max concurrent active automations / user'),
    help: 'Per-user cap on simultaneously active automations.',
  },
  {
    key: 'requireApprovalAboveUserCap',
    label: 'Require Admin Approval Above User Cap',
    section: 'automations',
    type: 'tri-state',
    default: '',
    visibleWhen: schedulingOn,
    options: [
      { label: 'Inherit (platform default: on)', value: '' },
      { label: 'On — over-cap requests go to admin approval', value: 'true' },
      { label: 'Off — over-cap requests are hard-rejected', value: 'false' },
    ],
    help: 'When on, a user requesting more than their per-user cap goes to admin approval (admin can authorise up to the company cap, never above). The company quota is always a hard ceiling.',
  },

  // --- Modules (rendered within Core) ---
  {
    key: 'numaOps',
    label: 'Numa Ops',
    section: 'core',
    type: 'switch',
    default: false,
    help: 'Enable Numa Ops (work management, kanban boards, CRM)',
  },

  // --- Bedrock & Billing ---
  {
    key: 'showCredits',
    label: 'Show Credits',
    section: 'bedrock',
    type: 'switch',
    default: true,
    help: "Surface the Credits dashboard (Settings) and the in-chat credit indicator to this client's users. Metering runs regardless; pricing is configured on the Credits page.",
  },
  {
    key: 'bedrockAccount',
    label: 'Bedrock Account',
    section: 'bedrock',
    type: 'text',
    default: '',
    help: "AWS account ID that THIS account will use for Bedrock quotas (instead of its own). Leave empty to use this account's own quota.",
  },
  {
    key: 'allowBedrockQuotaSharing',
    label: 'Allow Bedrock Quota Sharing',
    section: 'bedrock',
    type: 'switch',
    default: false,
    help: "When enabled, OTHER Numa accounts can use THIS account's Bedrock quotas",
  },
  {
    key: 'useGlobalInferenceProfile',
    label: 'Use Global Bedrock Inference Profile',
    section: 'bedrock',
    type: 'switch',
    default: true,
    help: 'On (default): route Sonnet/Opus 4.5+ via the global.* Bedrock inference profile, avoiding the 10% per-token regional CRI premium. Off: stay on us./au./apac.* — required for customers whose parent-org SCPs deny the global route.',
  },

  // --- Security & SSO ---
  {
    key: 'ssoEnabled',
    label: 'SSO Self-Service',
    section: 'security',
    type: 'switch',
    default: true,
    help: 'Show SSO admin tab so admins can configure SAML 2.0 identity providers',
  },
  {
    key: 'ssoEnterprise',
    label: 'SSO Enterprise (SCIM/OIDC)',
    section: 'security',
    type: 'switch',
    default: false,
    help: 'Group mapping, OIDC, SSO-only, SCIM. Requires SSO Self-Service enabled.',
  },
  {
    key: 'mfa',
    label: 'Multi-Factor Authentication (MFA)',
    section: 'security',
    type: 'switch',
    default: false,
    help: 'Require TOTP-based two-factor authentication for all users',
  },

  // --- Development (internal / in-progress features; rendered in the Development
  //     section at the bottom of the form — several are NOT customer-ready) ---
  {
    key: 'devInstance',
    label: 'Development Instance',
    section: 'development',
    type: 'switch',
    default: false,
    help: 'INTERNAL ONLY — marks this stack as an Arcanum dev/demo environment (dev apps, power-user UI). Never enable for a customer.',
  },
  {
    key: 'numaVoice',
    label: 'Numa Voice',
    section: 'development',
    type: 'switch',
    default: false,
    help: 'Amazon Connect SDR telephony (softphone, recordings, AI call intelligence). Requires Numa Ops.',
  },
  {
    key: 'connectAutoProvision',
    label: 'Connect Auto-Provision',
    section: 'development',
    type: 'switch',
    default: false,
    visibleWhen: voiceOn,
    childOf: 'numaVoice',
    help: 'Create the Amazon Connect instance via IaC on next deploy (Phase 2). Off = manually-created instance.',
  },
  {
    key: 'connectClaimDid',
    label: 'Connect Claim DID',
    section: 'development',
    type: 'switch',
    default: false,
    visibleWhen: voiceOn,
    childOf: 'numaVoice',
    help: 'Claim a BILLABLE phone number at deploy time (only with auto-provision). Off = claim numbers via the in-app voice admin.',
  },
  {
    key: 'connectInstanceUrl',
    label: 'Connect Instance URL',
    section: 'development',
    type: 'text',
    default: '',
    visibleWhen: voiceOn,
    childOf: 'numaVoice',
    placeholder: 'https://numa-<client>.my.connect.aws',
    help: 'Instance access URL — leave blank when auto-provision derives it. Do NOT paste the /ccp-v2 URL.',
  },
  {
    key: 'voiceLiveAssist',
    label: 'Live Assist (Contact Lens)',
    section: 'development',
    type: 'switch',
    default: false,
    visibleWhen: voiceOn,
    childOf: 'numaVoice',
    help: 'Real-time Contact Lens analytics → live in-call SDR assist (suggested objection rebuttals). BILLED per analyzed minute (metered to credits). Off = post-call summary/sentiment only.',
  },
  {
    key: 'v2Apps',
    label: 'V2 Apps (not ready for customers)',
    section: 'development',
    type: 'switch',
    default: false,
    help: 'Enable V2 Apps. Internal/dev only — not ready for customer use.',
  },
  {
    key: 'synergy',
    label: 'Synergy 12d (connector + cross-job search)',
    section: 'development',
    type: 'switch',
    default: false,
    help: 'Master switch for all Synergy functionality — file browser, cross-job AI search, crawl. Requires Native Integrations + a Bedrock KB.',
  },
  {
    key: 'numaDropZones',
    label: 'Drop Zones',
    section: 'development',
    type: 'switch',
    default: false,
    help: 'Allow users to create shared upload folders for external users',
  },
  {
    key: 'numaSharing',
    label: 'Sharing',
    section: 'development',
    type: 'switch',
    default: false,
    help: 'Allow users to share documents externally for Q&A',
  },
];

export const findField = (key: string): FieldDef | undefined => FIELDS.find((f) => f.key === key);

export const fieldsForSection = (section: SectionId): FieldDef[] => FIELDS.filter((f) => f.section === section);

// ---------------------------------------------------------------------------
// Hydration / serialization / diff helpers
// ---------------------------------------------------------------------------

/** Form value for a field given a config record (or undefined → defaults). */
export function hydrateFieldValue(def: FieldDef, config: Partial<ClientConfig> | undefined): FormFieldValue {
  if (def.hydrate) return def.hydrate(config);
  const raw = (config as Record<string, unknown> | undefined)?.[def.key];
  switch (def.type) {
    case 'switch':
      return typeof raw === 'boolean' ? raw : (def.default as boolean);
    case 'tri-state':
      return typeof raw === 'boolean' ? String(raw) : '';
    case 'number':
      return raw != null ? String(raw) : '';
    case 'text':
      return typeof raw === 'string' ? raw : (def.default as string);
    case 'select':
      return typeof raw === 'string' ? raw : (def.default as string);
  }
}

/** Initial form values for a whole record (Create mode passes undefined). */
export function hydrateFormValues(config: Partial<ClientConfig> | undefined): FormValues {
  const values: FormValues = {};
  for (const def of FIELDS) values[def.key] = hydrateFieldValue(def, config);
  return values;
}

/** Create-page initial values: schema defaults, overridden by createDefault. */
export function createModeInitialValues(): FormValues {
  const values: FormValues = {};
  for (const def of FIELDS) values[def.key] = def.createDefault ?? hydrateFieldValue(def, undefined);
  return values;
}

/**
 * The config value a form value serializes to. `undefined` means "key absent"
 * (in Update mode that becomes a REMOVE expression via updateClientConfig).
 */
export function serializeFieldValue(def: FieldDef, value: FormFieldValue): unknown {
  switch (def.type) {
    case 'switch':
      return Boolean(value);
    case 'tri-state':
      return value === '' ? undefined : value === 'true';
    case 'number': {
      const s = String(value).trim();
      if (!s) return undefined;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? undefined : n;
    }
    case 'text': {
      const s = String(value).trim();
      return s === '' ? undefined : s;
    }
    case 'select': {
      const s = String(value);
      return s === '' ? undefined : s;
    }
  }
}

/**
 * The effective current value of a field for diffing — what the deployment
 * would use today. Switches/selects fall back to the display default when
 * the key is absent; optional text/number/tri-state fields stay undefined.
 */
export function effectiveCurrentValue(def: FieldDef, config: Partial<ClientConfig> | undefined): unknown {
  // Fields with a derived absent-value (e.g. defaultChatModel) resolve it here
  // so a pristine record that only inherits the derived value shows no diff.
  if (def.effectiveCurrent) return def.effectiveCurrent(config);
  const raw = (config as Record<string, unknown> | undefined)?.[def.key];
  switch (def.type) {
    case 'switch':
      return typeof raw === 'boolean' ? raw : (def.default as boolean);
    case 'select':
      return typeof raw === 'string' ? raw : serializeFieldValue(def, def.default);
    case 'tri-state':
    case 'number':
    case 'text':
      return raw ?? undefined;
  }
}

/** True when the field's form value differs from its blank-record default. */
export function isNonDefault(def: FieldDef, value: FormFieldValue): boolean {
  return value !== hydrateFieldValue(def, undefined);
}

/**
 * An ON switch that Update/Create should force-write as an explicit `true`.
 *
 * On-by-default flags (switches with `default: true`) never appear in a stored
 * record when they simply inherit their default — the deployment reads `true`,
 * but config.json looks like the feature is unset. To keep config.json an honest
 * mirror of what's enabled, both payload builders materialize every switch that
 * is currently ON as `true`. OFF switches stay absent (writing every `false`
 * flag would bloat the record). Callers still guard on gate visibility and, in
 * Update mode, on the key being absent from the record.
 */
export function isOnSwitch(def: FieldDef, value: FormFieldValue): boolean {
  return def.type === 'switch' && value === true;
}

// ---------------------------------------------------------------------------
// Deprecated / dead keys — drives the Config Hygiene panel and the bulk
// tool's "Deprecated — cleanup" group. Removal uses the targeted REMOVE path
// (updateClientConfig with an undefined value), which does not require the
// remaining record to pass full-schema validation.
// ---------------------------------------------------------------------------

export interface DeprecatedKeyDef {
  key: string;
  reason: string;
  /** Extra caution when removal could change behavior for this record. */
  warnIf?: (config: Record<string, unknown>) => string | null;
}

const synergyFallbackWarning = (config: Record<string, unknown>): string | null => {
  // infra falls back to `synergy ?? (synergyKbCrawl || synergyFileParity)` —
  // removing a true legacy flag before `synergy` is set would turn Synergy off.
  if (config['synergy'] === undefined && (config['synergyKbCrawl'] === true || config['synergyFileParity'] === true)) {
    return 'This record relies on the legacy Synergy fallback. Set "synergy": true first, then remove this key.';
  }
  return null;
};

export const DEPRECATED_KEYS: DeprecatedKeyDef[] = [
  {
    key: 'numaChatAgents',
    reason: 'Gated the deprecated Chat V1 agent; nothing reads it any more.',
  },
  {
    key: 'knowledgeBases',
    reason: 'Never read — the frontend KNOWLEDGE_BASES flag is hard-coded true at deploy time.',
  },
  {
    key: 'recordsKBEnabled',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'contentSearchEnabled',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'numaFiles',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'transcriptionService',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'jobHistory',
    reason: 'JOB_HISTORY is emitted to config.json but never read by the frontend.',
  },
  {
    key: 'synergyTermIndex',
    reason: 'Deprecated Synergy sub-flag; no longer read anywhere.',
  },
  {
    key: 'synergyFileParity',
    reason: 'Deprecated — replaced by the single "synergy" flag.',
    warnIf: synergyFallbackWarning,
  },
  {
    key: 'synergyKbCrawl',
    reason: 'Deprecated — replaced by the single "synergy" flag.',
    warnIf: synergyFallbackWarning,
  },
  {
    key: 'secretsVaultEnabled',
    reason: 'Deprecated (TASK-146) — the Secrets Vault follows Native Integrations; this value is ignored.',
  },
  {
    key: 'developerMode',
    reason: 'Merged into devInstance (FEAT-239) — Development Instance now also enables the power-user UI.',
    // Always warn (and stay out of "Remove all") until the merge is deployed
    // fleet-wide — an older image still reads this flag.
    warnIf: () =>
      'Remove only once this client runs an image with the FEAT-239 merge — older images still read this flag.',
  },
  {
    key: 'oauthIntegrationsEnabled',
    reason:
      'Merged into dataConnectorsEnabled (FEAT-239) — Native Integrations now gates the vault-backed OAuth connectors too.',
    warnIf: () =>
      'Remove only once this client runs an image with the FEAT-239 merge — older images still read this flag.',
  },
  {
    key: 'oauthProviders',
    reason: 'Legacy OAuth cloud-storage provider config, superseded by COMPANY vault secrets; nothing reads it.',
  },
  {
    key: 'enableOpenApiDocs',
    reason: 'Schema key exists but the deployment stack never wires it through; setting it has no effect.',
  },
  {
    key: 'logGroup',
    reason: 'Internal infra prop that leaked into the schema; must never be persisted in a client config.',
  },
];

export const DEPRECATED_KEY_SET = new Set(DEPRECATED_KEYS.map((d) => d.key));

/** Every key the portal schema knows (deprecated keys included). */
export const KNOWN_CONFIG_KEYS = new Set(Object.keys(clientConfigSchema.shape));

export interface HygieneIssue {
  key: string;
  kind: 'deprecated' | 'unknown';
  reason: string;
  warning?: string;
  value: unknown;
}

/** Compute hygiene issues (deprecated + unknown keys) present in a record. */
export function computeHygieneIssues(config: Record<string, unknown>): HygieneIssue[] {
  const issues: HygieneIssue[] = [];
  for (const def of DEPRECATED_KEYS) {
    if (config[def.key] !== undefined) {
      issues.push({
        key: def.key,
        kind: 'deprecated',
        reason: def.reason,
        warning: def.warnIf?.(config) ?? undefined,
        value: config[def.key],
      });
    }
  }
  for (const key of Object.keys(config)) {
    // Deprecated keys already reported above — a key that has also been
    // deleted from the schema (e.g. developerMode) must not double-report.
    if (!KNOWN_CONFIG_KEYS.has(key) && !DEPRECATED_KEY_SET.has(key) && config[key] !== undefined) {
      issues.push({
        key,
        kind: 'unknown',
        reason: 'Not in the portal schema — possibly a typo, or a key from a newer schema version.',
        value: config[key],
      });
    }
  }
  return issues;
}

// ---------------------------------------------------------------------------
// Bulk Update tool derivation
// ---------------------------------------------------------------------------

export interface BulkFieldConfig {
  key: string;
  label: string;
  type: 'boolean' | 'enum' | 'string' | 'number' | 'json';
  options?: { label: string; value: string }[];
  description: string;
  allowUnset?: boolean;
  /** Deprecated-key cleanup entries: the ONLY valid operation is Unset. */
  removeOnly?: boolean;
  removeOnlyReason?: string;
}

/** Fields that exist only in the bulk tool (no single-client form control). */
const BULK_ONLY_FIELDS: BulkFieldConfig[] = [
  {
    key: 'customDomain',
    label: 'Custom Domain',
    type: 'string',
    allowUnset: true,
    description: 'Custom domain name — use Unset to fall back to the auto-generated domain',
  },
  {
    key: 'apps',
    label: 'Apps Configuration',
    type: 'json',
    description: 'JSON object mapping app IDs to config (e.g. {"meeting-analyser": {}})',
  },
  {
    key: 'allApps',
    label: 'Deploy All Apps',
    type: 'boolean',
    description: 'Deploy every app in the library (recommended for dev/demo stacks)',
  },
  {
    key: 'allProdApps',
    label: 'All Production Apps',
    type: 'boolean',
    description: 'Deploy all production applications',
  },
  {
    key: 'groups',
    label: 'Groups (Advanced)',
    type: 'json',
    allowUnset: true,
    description: 'JSON override for feature groups (e.g. {"standard":["chat"],"admin":["chat","manageUsers"]})',
  },
];

function bulkTypeFor(def: FieldDef): BulkFieldConfig['type'] {
  switch (def.type) {
    case 'switch':
      return 'boolean';
    case 'tri-state':
      return 'boolean';
    case 'number':
      return 'number';
    case 'select':
      return 'enum';
    case 'text':
      return 'string';
  }
}

function bulkAllowUnset(def: FieldDef): boolean {
  // region is schema-required; alwaysWrite keys are re-written by the Update
  // page on every save, so unsetting them in bulk would just churn.
  if (def.key === 'region' || def.alwaysWrite) return false;
  // Anything that serializes '' to "key absent" is unset-able, as are the
  // inherit-from-platform quota fields and tri-state (inherit) selects.
  return def.type !== 'switch';
}

/**
 * The bulk tool's field list, derived from the registry: every form field
 * (minus explicit bulk:false), plus the bulk-only JSON/string fields, plus
 * remove-only cleanup entries for the deprecated keys.
 */
export function getBulkUpdatableFields(): BulkFieldConfig[] {
  const fromRegistry: BulkFieldConfig[] = FIELDS.filter((f) => f.bulk !== false).map((def) => ({
    key: def.key,
    label: def.label,
    type: bulkTypeFor(def),
    // Selects with an '' (unset) option rely on the Unset checkbox in bulk —
    // never write '' as a real value.
    options: def.options?.filter((o) => o.value !== ''),
    description: def.help ?? def.label,
    allowUnset: bulkAllowUnset(def),
  }));
  const cleanup: BulkFieldConfig[] = DEPRECATED_KEYS.map((d) => ({
    key: d.key,
    label: `${d.key} (deprecated)`,
    type: 'json',
    description: d.reason,
    allowUnset: true,
    removeOnly: true,
    removeOnlyReason: d.reason,
  }));
  return [...fromRegistry, ...BULK_ONLY_FIELDS, ...cleanup];
}
